import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createIdea } from '../services/ideaService';
import { ExclamationCircleIcon } from '@heroicons/react/solid';

// Validation schema for idea creation
const ideaSchema = z.object({
  title: z.string()
    .min(5, 'Title must be at least 5 characters')
    .max(200, 'Title cannot exceed 200 characters'),
  
  content: z.string()
    .min(20, 'Description must be at least 20 characters')
    .max(2000, 'Description cannot exceed 2000 characters'),
  
  tags: z.string()
    .optional()
    .transform(tags => tags ? tags.split(',').map(tag => tag.trim()).filter(Boolean) : []),
  
  isAnonymous: z.boolean()
});

const CreateIdea = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const { 
    register, 
    handleSubmit, 
    formState: { errors },
    
  } = useForm({
    resolver: zodResolver(ideaSchema),
    defaultValues: {
      isAnonymous: false,
      tags: ''
    }
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const newIdea = await createIdea({
        ...data,
        tags: data.tags || []
      });
      navigate(`/idea/${newIdea._id}`);
    } catch (error) {
      console.error('Create idea error:', error);
      setSubmitError(error.message || 'Failed to create idea');
      setIsSubmitting(false);
    }
  };

  // Prevent access if not authenticated
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto bg-white shadow-md rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Create a New Innovation Idea
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title Input */}
          <div>
            <label 
              htmlFor="title" 
              className="block text-sm font-medium text-gray-700"
            >
              Idea Title
            </label>
            <input
              id="title"
              type="text"
              {...register('title')}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                focus:border-brand-500 focus:ring-brand-500 sm:text-sm 
                ${errors.title ? 'border-red-500' : ''}`}
              placeholder="Enter a concise title for your idea"
            />
            {errors.title && (
              <p className="mt-2 text-sm text-red-600">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Content/Description Input */}
          <div>
            <label 
              htmlFor="content" 
              className="block text-sm font-medium text-gray-700"
            >
              Detailed Description
            </label>
            <textarea
              id="content"
              {...register('content')}
              rows={5}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                focus:border-brand-500 focus:ring-brand-500 sm:text-sm 
                ${errors.content ? 'border-red-500' : ''}`}
              placeholder="Describe your innovation idea in detail"
            />
            {errors.content && (
              <p className="mt-2 text-sm text-red-600">
                {errors.content.message}
              </p>
            )}
          </div>

          {/* Tags Input */}
          <div>
            <label 
              htmlFor="tags" 
              className="block text-sm font-medium text-gray-700"
            >
              Tags (Optional, comma-separated)
            </label>
            <input
              id="tags"
              type="text"
              {...register('tags')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              placeholder="e.g. innovation, process, technology"
            />
            <p className="mt-2 text-xs text-gray-500">
              Separate tags with commas
            </p>
          </div>

          {/* Anonymous Posting Toggle */}
          <div className="flex items-center">
            <input
              id="isAnonymous"
              type="checkbox"
              {...register('isAnonymous')}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
            />
            <label 
              htmlFor="isAnonymous" 
              className="ml-2 block text-sm text-gray-900"
            >
              Post Anonymously
            </label>
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon 
                    className="h-5 w-5 text-red-400" 
                    aria-hidden="true" 
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-5">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create Idea'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIdea;

